/**
 * Clients Service
 * Handles client-related operations including Priority sync
 */
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  getDocs,
  limit,
  documentId,
  serverTimestamp,
} from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'
import { callFunction } from '../firebase/functions.js'
import logger from './logger.js'
import {
  hasMarketingConsent,
  buildMarketingConsentPayload,
  buildMarketingConsentUpdate,
} from '@core/utils/consent.js'

const log = logger.createScoped('ClientsService')

// Collection name
const CLIENTS_COLLECTION = 'ClientsV2'

// 10 minutes timeout to match Cloud Function's 540s limit
const CLIENTS_PULL_TIMEOUT = 600000

/**
 * Pull clients from Priority ERP and sync to Firestore
 * Calls the priorityPullClients callable function
 * @returns {Promise<Object>} Result with totalFetched, realClientsUpserted, realClientsDeleted, assortmentsUpserted, failedCount, durationMs, errors
 */
export async function pullClientsFromPriority() {
  log.info('Starting Priority clients pull')

  try {
    const result = await callFunction('priorityPullClients', {}, { timeout: CLIENTS_PULL_TIMEOUT })

    log.info('Priority clients pull completed', {
      totalFetched: result.totalFetched,
      realClientsUpserted: result.realClientsUpserted,
      realClientsDeleted: result.realClientsDeleted,
      assortmentsUpserted: result.assortmentsUpserted,
      failedCount: result.failedCount,
      durationMs: result.durationMs,
    })

    return result
  } catch (error) {
    log.error('Priority clients pull failed', error)
    throw error
  }
}

/**
 * Subscribe to realtime updates of ClientsV2 collection
 * @param {Function} callback - Callback receiving array of client docs and optional error
 * @returns {Function} Unsubscribe function
 */
export function subscribeClients(callback) {
  log.debug('Subscribing to ClientsV2 collection')

  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  // Default sort by makat (ascending) for deterministic order
  const q = query(clientsRef, orderBy('makat', 'asc'))

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const clients = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      log.debug('Clients updated', { count: clients.length })
      callback(clients, null)
    },
    (error) => {
      log.error('Clients subscription error', error)
      callback([], error)
    }
  )

  return unsubscribe
}

/**
 * Update a client document in Firestore
 * @param {string} clientId - The client document ID
 * @param {Object} data - Data to update
 * @returns {Promise<void>}
 */
export async function updateClient(clientId, data) {
  log.info('Updating client', { clientId, data })

  try {
    const db = getFirebaseDb()
    const clientRef = doc(db, CLIENTS_COLLECTION, clientId)
    await updateDoc(clientRef, data)
    log.info('Client updated successfully', { clientId })
  } catch (error) {
    log.error('Failed to update client', error)
    throw error
  }
}

/**
 * Subscribe to realtime updates of ClientsV2 filtered by agent number
 * @param {number} agentNumber - The agent number to filter by
 * @param {Function} callback - Callback receiving array of client docs and optional error
 * @param {Object} options - Optional settings
 * @param {Function} options.onError - Error callback
 * @returns {Function} Unsubscribe function
 */
export function subscribeClientsByAgent(agentNumber, callback, options = {}) {
  log.debug('Subscribing to ClientsV2 for agent', { agentNumber })

  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const q = query(clientsRef, where('agent', '==', agentNumber), orderBy('makat', 'asc'))

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const clients = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      log.debug('Agent clients updated', { agentNumber, count: clients.length })
      callback(clients, null)
    },
    (error) => {
      log.error('Agent clients subscription error', { agentNumber, error })
      if (options.onError) {
        options.onError(error)
      }
      callback([], error)
    }
  )

  return unsubscribe
}

/**
 * Get a client document by makat field (not doc ID)
 * @param {string|number} makat - The client makat to search for
 * @returns {Promise<Object|null>} Client document with id, or null if not found
 */
export async function getClientByMakat(makat) {
  if (!makat) {
    log.warn('getClientByMakat called without makat')
    return null
  }

  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const q = query(clientsRef, where('makat', '==', String(makat)), limit(1))

  try {
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      log.debug('Client not found by makat', { makat })
      return null
    }

    const doc = snapshot.docs[0]
    const client = {
      id: doc.id,
      ...doc.data(),
    }

    log.debug('Client found by makat', { makat, docId: doc.id })
    return client
  } catch (error) {
    log.error('Failed to get client by makat', { makat, error })
    throw error
  }
}

/**
 * Fetch all ClientsV2 clients who consented to marketing (`marketing.consent == true`).
 * During the tos→marketing migration it also reads the legacy `tos == true` docs and merges
 * by id (a doc that has the nested `marketing` object is judged by it alone).
 * One-time fetch (not realtime).
 * @returns {Promise<Object[]>} Array of client docs with id
 */
export async function fetchMarketingApprovedClients() {
  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const qNew = query(clientsRef, where('marketing.consent', '==', true))
  const qLegacy = query(clientsRef, where('tos', '==', true))

  try {
    const [snapNew, snapLegacy] = await Promise.all([getDocs(qNew), getDocs(qLegacy)])
    const byId = new Map()
    for (const d of [...snapNew.docs, ...snapLegacy.docs]) {
      const data = { id: d.id, ...d.data() }
      if (!byId.has(d.id) && hasMarketingConsent(data)) byId.set(d.id, data)
    }
    const clients = [...byId.values()]
    log.debug('Marketing-approved clients fetched', { count: clients.length })
    return clients
  } catch (error) {
    log.error('Failed to fetch marketing-approved clients', error)
    throw error
  }
}

/**
 * Fetch clients who actively said NO to marketing: declined in the app / by an admin, or
 * removed themselves via the WhatsApp unsubscribe link (`marketing.answered == true` with
 * `marketing.consent == false`). Customers who were never asked are NOT included.
 * Single-field equality query + in-memory filter, so no composite index is needed.
 * One-time fetch (not realtime).
 * @returns {Promise<Object[]>} Array of client docs with id
 */
export async function fetchMarketingDeclinedClients() {
  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const q = query(clientsRef, where('marketing.answered', '==', true))

  try {
    const snap = await getDocs(q)
    const clients = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((c) => !hasMarketingConsent(c))
    log.debug('Marketing-declined clients fetched', { count: clients.length })
    return clients
  } catch (error) {
    log.error('Failed to fetch marketing-declined clients', error)
    throw error
  }
}

/**
 * Record a marketing-consent answer on a ClientsV2 doc — the ONE writer shared by the
 * client popup, the profile toggle, the admin consent panel and the Divur screen
 * (migration plan step 7). Writes the `marketing` fields with dotted paths — a MERGE, so
 * `marketing.unsubscribedAt` (stamped only by the unsubscribe function) always survives —
 * plus the legacy flat `tos*` fields until the migration ends.
 * @param {{ clientId: string, consent: boolean, uid: string|null, source: string, previousMarketing?: Object|null }} params
 * @returns {Promise<Object>} the resulting `marketing` object, for patching local state
 *   (dates are serverTimestamp sentinels; `previousMarketing` supplies the untouched fields)
 */
export async function recordMarketingConsent({
  clientId,
  consent,
  uid,
  source,
  previousMarketing = null,
}) {
  await updateClient(clientId, {
    ...buildMarketingConsentUpdate({ consent, uid, source }),
    // Legacy dual-write — remove in migration step 7
    tos: consent === true,
    tos_date: serverTimestamp(),
    tos_user: uid || null,
    tos_answered: true,
    tos_answered_source: source,
  })
  return buildMarketingConsentPayload({ consent, uid, source, previous: previousMarketing })
}

/**
 * Fetch unique city values from ClientsV2 collection.
 * Lightweight one-time fetch for dropdown population.
 * @returns {Promise<string[]>} Sorted array of unique city names
 */
/**
 * Fetch ClientsV2 docs by makat (doc id), batched `documentId() in` reads (30 per query).
 * @param {string[]} makats
 * @returns {Promise<Map<string, Object>>} makat → client doc (with id)
 */
export async function fetchClientsByMakats(makats) {
  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const unique = [...new Set((makats || []).map(String).filter(Boolean))]
  const chunks = []
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30))
  const snaps = await Promise.all(
    chunks.map((chunk) => getDocs(query(clientsRef, where(documentId(), 'in', chunk))))
  )
  const result = new Map()
  snaps.forEach((snap) => snap.docs.forEach((d) => result.set(d.id, { id: d.id, ...d.data() })))
  return result
}

export async function fetchUniqueCities() {
  const db = getFirebaseDb()
  const clientsRef = collection(db, CLIENTS_COLLECTION)
  const snapshot = await getDocs(clientsRef)

  const cities = new Set()
  snapshot.docs.forEach((d) => {
    const city = d.data().city
    if (city) cities.add(city)
  })

  return Array.from(cities).sort((a, b) => a.localeCompare(b, 'he'))
}
